import { ArrowSquareOut, BookOpen } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";

import { Preview } from "../../../components/Preview";
import { knowledgeDatabaseRoute } from "../../settings/routes/knowledge-database/route";
import { useGetKnowledgeDataRecord } from "../../../utils/queries/knowledge-db";
import { buildKnowledgeRecordSearchQuery } from "../../../utils/parsers/knowledge-db";

export function KnowledgeRecordContextPanel({
  recordId,
  onClose,
}: {
  recordId: string;
  onClose?: () => void;
}) {
  const query = useGetKnowledgeDataRecord(recordId);

  if (query.isLoading) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center p-6"
        data-testid="knowledge-record-context-loading"
      >
        <Text size="2" className="text-puntt-neutral-gray-11">
          Loading approved claim…
        </Text>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col gap-3 p-4"
        data-testid="knowledge-record-context-error"
      >
        <Text size="2" className="text-puntt-neutral-gray-11">
          Could not load this approved claim. It may have been removed from the
          Knowledge Database.
        </Text>
        {onClose ? (
          <Button
            size="1"
            variant="soft"
            color="gray"
            onClick={onClose}
            data-testid="knowledge-record-context-back"
          >
            Back to context assets
          </Button>
        ) : null}
      </div>
    );
  }

  const record = query.data;
  const fields = record.data ?? [];
  const knowledgeDatabaseSearch = {
    tab: record.modelId,
    recordId: record._id,
    q: buildKnowledgeRecordSearchQuery(fields),
    pageSize: 100,
  };

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="knowledge-record-context-panel"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-puntt-neutral-gray-6 px-3 py-2">
        <Flex gap="2" align="center">
          <BookOpen size={16} className="text-puntt-accent-11" aria-hidden />
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
            Approved claim
          </Text>
        </Flex>
        <Flex gap="2" align="center">
          <Button size="1" variant="outline" asChild>
            <Link
              to={knowledgeDatabaseRoute.to}
              search={knowledgeDatabaseSearch}
              target="_blank"
              rel="noreferrer"
              className="puntt-link inline-flex items-center gap-1"
              data-testid="knowledge-record-context-knowledge-database-link"
            >
              <Text size="1">Knowledge Database</Text>
              <ArrowSquareOut size={12} aria-hidden />
            </Link>
          </Button>
          {onClose ? (
            <Button
              size="1"
              variant="soft"
              color="gray"
              onClick={onClose}
              data-testid="knowledge-record-context-back"
            >
              Back
            </Button>
          ) : null}
        </Flex>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="rounded-md border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-3">
          <Preview knowledgeDataRecord={fields} />
        </div>
      </div>
    </div>
  );
}
